/**
 * Component for displaying and managing API v1 jobs
 * Shows job status, progress, and download links for results stored in Supabase Storage
 */

import React, { useState, useEffect, useCallback } from 'react'
import { APIv1Client, JobStatus, SubscriptionResponse, UsageResponse } from '../lib/api_v1'
import { Download, RefreshCw, Clock, CheckCircle, XCircle, Loader, LogIn, UserPlus, CreditCard, Settings } from 'lucide-react'
import Auth from './Auth'
import { useLocale } from '../i18n/LocaleContext'

interface APIv1JobsProps {
  apiBaseUrl?: string
  apiKey?: string
}

interface AuthResponse {
  access_token: string
  api_key: string
  user: {
    id: string
    email: string
    tier: string
    subscription_status: string
    api_key: string
  }
}

const APIv1Jobs: React.FC<APIv1JobsProps> = ({ 
  apiBaseUrl,
  apiKey 
}) => {
  const { t, dateLocale } = useLocale()
  // Use environment variable or relative path for production
  const defaultApiBase = import.meta.env.VITE_API_BASE || ''
  const effectiveApiBase = apiBaseUrl || defaultApiBase
  const [client, setClient] = useState<APIv1Client | null>(null)
  const [jobs, setJobs] = useState<JobStatus[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [apiKeyInput, setApiKeyInput] = useState(apiKey || '')
  const [showAuth, setShowAuth] = useState(false)
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login')
  const [authLoading, setAuthLoading] = useState(false)
  const [user, setUser] = useState<AuthResponse['user'] | null>(null)
  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null)
  const [usage, setUsage] = useState<UsageResponse | null>(null)
  const [loadingBilling, setLoadingBilling] = useState(false)
  const [billingError, setBillingError] = useState<string | null>(null)
  const [connectionError, setConnectionError] = useState(false)

  useEffect(() => {
    const apiClient = new APIv1Client(effectiveApiBase, apiKey)
    setClient(apiClient)
    if (apiKey) {
      apiClient.setApiKey(apiKey)
    }
  }, [effectiveApiBase, apiKey])

  const loadJobs = useCallback(async () => {
    if (!client) return
    
    setLoading(true)
    setError(null)
    
    try {
      const jobList = await client.listJobs(20)
      setJobs(jobList)
      setConnectionError(false) // Connection successful
    } catch (err: any) {
      // Check for connection errors
      const errorMessage = err.message || ''
      const isConnectionError = 
        errorMessage.includes('ERR_CONNECTION_REFUSED') ||
        errorMessage.includes('Failed to fetch') ||
        errorMessage.includes('NetworkError') ||
        errorMessage.includes('ECONNREFUSED')
      
      if (isConnectionError) {
        // Server is not available - don't show error, just mark connection as failed
        setConnectionError(true)
        setError(null)
        setJobs([])
        return // Don't set loading to false yet, will be set in finally
      }
      
      // If unauthorized (401), user needs to register/login
      if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
        setError(null) // Don't show error, just show empty list
        setJobs([])
        setConnectionError(false)
      } else if (errorMessage.includes('404') || errorMessage.includes('not found')) {
        // If endpoint doesn't exist, show empty list
        setJobs([])
        setConnectionError(false)
      } else {
        setError(err.message || t('api.error.generic'))
        setConnectionError(false)
      }
    } finally {
      setLoading(false)
    }
  }, [client, t])

  useEffect(() => {
    if (client) {
      loadJobs()
      // Poll for updates every 10 seconds (increased from 5s)
      // Only poll if connection is available
      const interval = setInterval(() => {
        // Skip polling if we have a connection error - wait for manual refresh
        if (!connectionError) {
          loadJobs()
        }
      }, 10000) // Increased to 10 seconds
      
      return () => {
        clearInterval(interval)
      }
    }
  }, [client, loadJobs, connectionError])

  const handleApiKeySubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    if (client && apiKeyInput.trim()) {
      client.setApiKey(apiKeyInput.trim())
      loadJobs()
    }
  }, [client, apiKeyInput, loadJobs])

  const handleRegister = async (emailValue: string, passwordValue: string, tierValue: string) => {
    setAuthLoading(true)
    setError(null)
    
    try {
      const response = await fetch(`${effectiveApiBase}/api/v1/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailValue, password: passwordValue, tier: tierValue })
      })
      
      if (!response.ok) {
        // Check for specific HTTP errors
        if (response.status === 502) {
          throw new Error('502: Backend server is not responding. Please check if the backend is running on the server.')
        } else if (response.status === 404) {
          throw new Error('404: API endpoint not found. This may indicate a configuration issue.')
        }
        const error = await response.json().catch(() => ({ detail: response.statusText }))
        throw new Error(error.detail || `${t('api.error.registerFailed')} (${response.status})`)
      }
      
      const data: AuthResponse = await response.json()
      setUser(data.user)
      setApiKeyInput(data.api_key)
      if (client) {
        client.setApiKey(data.api_key)
      }
      setShowAuth(false)
      loadJobs()
      // Load billing data after registration
      if (client) {
        loadBillingData()
      }
    } catch (err: any) {
      const errorMessage = err.message || t('api.error.registerFailed')
      
      // Check for specific HTTP errors (already handled in response.ok check, but double-check)
      if (errorMessage.includes('502') || errorMessage.includes('Bad Gateway')) {
        setError(t('api.error.server'))
      } else if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
        setError(t('api.error.notFound'))
      } else if (errorMessage.includes('Failed to fetch') || 
          errorMessage.includes('ERR_CONNECTION_REFUSED') ||
          errorMessage.includes('NetworkError') ||
          errorMessage.includes('ECONNREFUSED')) {
        setError(t('api.error.connection'))
      } else {
        setError(errorMessage)
      }
    } finally {
      setAuthLoading(false)
    }
  }

  const handleLogin = async (emailValue: string, passwordValue: string) => {
    setAuthLoading(true)
    setError(null)
    
    try {
      const response = await fetch(`${effectiveApiBase}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailValue, password: passwordValue })
      })
      
      if (!response.ok) {
        // Check for specific HTTP errors
        if (response.status === 502) {
          throw new Error('502: Backend server is not responding. Please check if the backend is running on the server.')
        } else if (response.status === 404) {
          throw new Error('404: API endpoint not found. This may indicate a configuration issue.')
        }
        const error = await response.json().catch(() => ({ detail: response.statusText }))
        throw new Error(error.detail || `${t('api.error.loginFailed')} (${response.status})`)
      }
      
      const data: AuthResponse = await response.json()
      setUser(data.user)
      setApiKeyInput(data.api_key)
      if (client) {
        client.setApiKey(data.api_key)
      }
      setShowAuth(false)
      loadJobs()
      // Load billing data after login
      if (client) {
        loadBillingData()
      }
    } catch (err: any) {
      const errorMessage = err.message || t('api.error.loginFailed')
      
      // Check for specific HTTP errors (already handled in response.ok check, but double-check)
      if (errorMessage.includes('502') || errorMessage.includes('Bad Gateway')) {
        setError(t('api.error.server'))
      } else if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
        setError(t('api.error.notFound'))
      } else if (errorMessage.includes('Failed to fetch') || 
          errorMessage.includes('ERR_CONNECTION_REFUSED') ||
          errorMessage.includes('NetworkError') ||
          errorMessage.includes('ECONNREFUSED')) {
        setError(t('api.error.connection'))
      } else {
        setError(errorMessage)
      }
    } finally {
      setAuthLoading(false)
    }
  }

  // Load billing data (subscription + usage)
  const loadBillingData = useCallback(async () => {
    if (!client) return
    
    setLoadingBilling(true)
    setBillingError(null)
    
    try {
      const [subData, usageData] = await Promise.all([
        client.getSubscription().catch(() => null),
        client.getUsage().catch(() => null)
      ])
      
      setSubscription(subData)
      setUsage(usageData)
    } catch (err: any) {
      // Non-critical errors (404 for subscription is OK)
      if (!err.message?.includes('No subscription found')) {
        setBillingError(err.message || t('api.error.billingLoad'))
      }
    } finally {
      setLoadingBilling(false)
    }
  }, [client])

  // Handle upgrade (redirect to Stripe checkout)
  const handleUpgrade = async (tier: string = 'hobby') => {
    if (!client) return
    
    try {
      const currentUrl = window.location.href.split('?')[0]
      const successUrl = `${currentUrl}?checkout=success`
      const cancelUrl = `${currentUrl}?checkout=cancel`
      
      const session = await client.createCheckoutSession(tier, successUrl, cancelUrl)
      
      // Redirect to Stripe checkout
      window.location.href = session.url
    } catch (err: any) {
      setBillingError(err.message || t('api.error.checkoutSession'))
    }
  }

  // Handle manage billing (redirect to Stripe portal)
  const handleManageBilling = async () => {
    if (!client) return
    
    try {
      const currentUrl = window.location.href.split('?')[0]
      const returnUrl = currentUrl
      
      const portal = await client.createPortalSession(returnUrl)
      
      // Redirect to Stripe portal
      window.location.href = portal.url
    } catch (err: any) {
      setBillingError(err.message || t('api.error.portalSession'))
    }
  }

  // Check for checkout success/cancel in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const checkout = params.get('checkout')
    
    if (checkout === 'success') {
      setError(null)
      // Reload billing data to reflect updated subscription
      if (client) {
        loadBillingData()
      }
      // Remove query param
      window.history.replaceState({}, '', window.location.pathname)
    } else if (checkout === 'cancel') {
      setError(t('api.error.checkoutCancelled'))
      // Remove query param
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [client, loadBillingData, t])

  // Load billing data when user is logged in
  useEffect(() => {
    if (user && client) {
      loadBillingData()
    }
  }, [user, client, loadBillingData])

  const handleDownload = async (job: JobStatus) => {
    if (!client || !job.result_url) return
    
    try {
      const filename = `job_${job.job_id}_result.parquet`
      await client.downloadResult(job.result_url, filename)
    } catch (err: any) {
      alert(t('api.error.download', { error: err.message }))
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return t('common.na')
    try {
      return new Date(dateString).toLocaleString(dateLocale)
    } catch {
      return dateString
    }
  }

  const getStatusIcon = (status: JobStatus['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />
      case 'running':
        return <Loader className="w-5 h-5 text-blue-500 animate-spin" />
      default:
        return <Clock className="w-5 h-5 text-gray-500" />
    }
  }

  const getStatusColor = (status: JobStatus['status']) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'failed':
        return 'bg-red-100 text-red-800'
      case 'running':
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (!client) {
    return <div className="p-4">{t('api.initializing')}</div>
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* Header Section */}
      <div className="mb-12">
        <h2 className="text-3xl font-bold mb-3 text-gray-900">{t('api.title')}</h2>
        <p className="text-gray-600 text-base leading-relaxed mb-6">
          {t('api.subtitle')}
        </p>
        {!connectionError && effectiveApiBase && (
          <p className="text-sm text-gray-500 mb-6">
            {t('api.connection.note', { url: effectiveApiBase })}
          </p>
        )}
        
        {/* Connection Error Warning - Subtle, at the top */}
        {connectionError && (
          <div className="mb-6 p-3 bg-amber-50 border-l-2 border-amber-400 rounded text-sm">
            <p className="text-amber-700">
              {t('api.error.connection')}{' '}
              ({effectiveApiBase || 'localhost:8001'})
            </p>
          </div>
        )}
      </div>
      
      {/* User Info */}
      {user && (
          <div className="mb-12 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-8 shadow-lg">
            <div className="flex flex-col lg:flex-row justify-between items-start gap-6">
              <div className="flex-1 space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('api.account.title')}</h3>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-700">
                      <span className="font-medium text-gray-900">{t('api.account.email')}</span> {user.email}
                    </p>
                    <div className="flex flex-wrap gap-4">
                      <p className="text-sm text-gray-700">
                        <span className="font-medium text-gray-900">{t('api.account.tier')}</span>{' '}
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {subscription?.tier || user.tier}
                        </span>
                      </p>
                      <p className="text-sm text-gray-700">
                        <span className="font-medium text-gray-900">{t('api.account.status')}</span>{' '}
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {subscription?.status || user.subscription_status}
                        </span>
                      </p>
                    </div>
                    {subscription?.current_period_end && (
                      <p className="text-sm text-gray-700">
                        <span className="font-medium text-gray-900">{t('api.account.periodEnds')}</span>{' '}
                        {new Date(subscription.current_period_end).toLocaleDateString(dateLocale)}
                      </p>
                    )}
                    <div className="pt-2">
                      <p className="text-xs font-medium text-gray-500 mb-1">{t('api.account.apiKey')}</p>
                      <p className="text-xs text-gray-700 font-mono bg-white px-3 py-2 rounded border border-gray-200 break-all">
                        {user.api_key}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Usage Stats */}
                {usage && usage.current_month && (
                  <div className="mt-6 pt-6 border-t border-green-200">
                    <h4 className="text-sm font-semibold text-gray-900 mb-4">{t('api.usage.title')}</h4>
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between text-sm text-gray-700 mb-2">
                          <span className="font-medium">{t('api.usage.reactions')}</span>
                          <span className="text-gray-600">
                            {usage.current_month.reactions || 0} / {usage.current_month.reactions_quota || '∞'}
                            <span className="ml-2 text-xs">({Math.round(usage.current_month.percentage_used || 0)}%)</span>
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-300" 
                            style={{ width: `${Math.min(usage.current_month.percentage_used || 0, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm text-gray-700 mb-2">
                          <span className="font-medium">{t('api.usage.apiCalls')}</span>
                          <span className="text-gray-600">
                            {usage.current_month.api_calls || 0} / {usage.current_month.api_calls_quota || '∞'}
                            <span className="ml-2 text-xs">({Math.round(((usage.current_month.api_calls || 0) / (usage.current_month.api_calls_quota || 1)) * 100)}%)</span>
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-300" 
                            style={{ width: `${Math.min(((usage.current_month.api_calls || 0) / (usage.current_month.api_calls_quota || 1)) * 100, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {billingError && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-xs text-red-700">{billingError}</p>
                  </div>
                )}
              </div>
              
              {/* Billing Actions */}
              <div className="flex flex-col gap-3 lg:ml-6">
                {user.subscription_status === 'trial' && (
                  <button
                    onClick={() => handleUpgrade('hobby')}
                    disabled={loadingBilling}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 disabled:opacity-50 transition-all shadow-md hover:shadow-lg font-medium whitespace-nowrap"
                  >
                    <CreditCard className="w-5 h-5" />
                    {t('api.billing.upgrade')}
                  </button>
                )}
                {user.subscription_status === 'active' && (
                  <button
                    onClick={handleManageBilling}
                    disabled={loadingBilling}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 transition-all shadow-md hover:shadow-lg font-medium whitespace-nowrap"
                  >
                    <Settings className="w-5 h-5" />
                    {t('api.billing.manage')}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        
      {/* Authentication Section - Modern Card Design */}
      {!user && (
        <div className="mb-16">
          {/* Auth Buttons Card */}
          {!showAuth && (
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-10 mb-10">
              <div className="text-center mb-10">
                <h3 className="text-2xl font-semibold text-gray-900 mb-3">{t('api.gate.title')}</h3>
                <p className="text-gray-600 text-base">{t('api.gate.subtitle')}</p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-5 justify-center mb-10">
                <button
                  onClick={() => {
                    setShowAuth(true)
                    setAuthMode('register')
                  }}
                  className="flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 font-medium text-base"
                >
                  <UserPlus className="w-5 h-5" />
                  {t('api.gate.register')}
                </button>
                <button
                  onClick={() => {
                    setShowAuth(true)
                    setAuthMode('login')
                  }}
                  className="flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 font-medium text-base"
                >
                  <LogIn className="w-5 h-5" />
                  {t('api.gate.login')}
                </button>
              </div>
              
              {/* Divider */}
              <div className="relative mb-10">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">{t('api.key.or')}</span>
                </div>
              </div>
              
              {/* API Key Input */}
              <form onSubmit={handleApiKeySubmit} className="max-w-2xl mx-auto">
                <label htmlFor="api-key-input" className="block text-sm font-medium text-gray-700 mb-4">
                  {t('api.key.label')}
                </label>
                <div className="flex gap-3">
                  <input
                    id="api-key-input"
                    type="password"
                    value={apiKeyInput}
                    onChange={(e) => setApiKeyInput(e.target.value)}
                    placeholder={t('api.key.placeholder')}
                    className="flex-1 px-5 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base transition-all"
                  />
                  <button
                    type="submit"
                    className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all font-medium shadow-md hover:shadow-lg"
                  >
                    {t('api.key.connect')}
                  </button>
                </div>
              </form>
            </div>
          )}
          
          {/* Auth Form */}
          {showAuth && (
            <div className="mb-10">
              <Auth
                mode={authMode}
                onModeChange={setAuthMode}
                onSubmit={async (emailValue, passwordValue, tierValue) => {
                  if (authMode === 'register') {
                    await handleRegister(emailValue, passwordValue, tierValue || 'hobby')
                  } else {
                    await handleLogin(emailValue, passwordValue)
                  }
                }}
                onCancel={() => {
                  setShowAuth(false)
                  setError(null)
                }}
                loading={authLoading}
                error={error}
              />
            </div>
          )}
        </div>
      )}

      {/* Refresh Button */}
      <div className="mb-8">
        <button
          onClick={loadJobs}
          disabled={loading}
          className="flex items-center gap-2 px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-all shadow-md hover:shadow-lg font-medium"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          {t('api.refresh')}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-8 p-5 bg-red-50 border-l-4 border-red-400 rounded-lg shadow-sm">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800 whitespace-pre-line">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Jobs List */}
      {jobs.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-12 text-center">
          <div className="max-w-md mx-auto">
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-lg text-gray-600">{t('api.empty.loading')}</p>
              </>
            ) : connectionError ? (
              <>
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-lg font-medium text-gray-900 mb-2">{t('api.empty.unavailable')}</p>
                <p className="text-gray-600">{t('api.empty.unavailableHint')}</p>
              </>
            ) : (
              <>
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-lg font-medium text-gray-900 mb-2">{t('api.empty.none')}</p>
                <p className="text-gray-600">{t('api.empty.noneHint')}</p>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {jobs.map((job) => (
            <div
              key={job.job_id}
              className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-200"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-start gap-4">
                  <div className="mt-1">
                    {getStatusIcon(job.status)}
                  </div>
                  <div className="space-y-2">
                    <h3 className="font-semibold text-xl text-gray-900">
                      {t('api.job.title', { id: `${job.job_id.slice(0, 8)}...` })}
                    </h3>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(job.status)}`}>
                      {job.status.toUpperCase()}
                    </span>
                  </div>
                </div>
                
                {job.status === 'completed' && job.result_url && (
                  <button
                    onClick={() => handleDownload(job)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all shadow-md hover:shadow-lg font-medium"
                  >
                    <Download className="w-5 h-5" />
                    {t('api.job.download')}
                  </button>
                )}
              </div>

              {/* Progress Bar */}
              {job.status === 'running' && (
                <div className="mb-6">
                  <div className="flex justify-between text-sm font-medium text-gray-700 mb-3">
                    <span>{t('api.job.progress')}</span>
                    <span className="text-blue-600">{job.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${job.progress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Error Message */}
              {job.error && (
                <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400 rounded-lg">
                  <div className="flex items-start">
                    <svg className="h-5 w-5 text-red-400 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-red-800">{t('api.job.error')}</p>
                      <p className="text-sm text-red-700 mt-1">{job.error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Result URL Info */}
              {job.result_url && (
                <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-400 rounded-lg">
                  <p className="text-sm font-medium text-green-800 mb-2">{t('api.job.resultUrl')}</p>
                  <a
                    href={job.result_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline break-all text-sm block"
                  >
                    {job.result_url.length > 80 ? `${job.result_url.slice(0, 80)}...` : job.result_url}
                  </a>
                  <p className="text-gray-600 mt-2 text-xs">
                    {t('api.job.storage')}
                  </p>
                </div>
              )}

              {/* Metadata */}
              <div className="flex flex-wrap gap-6 text-sm text-gray-600 pt-4 border-t border-gray-200">
                <div>
                  <span className="font-medium text-gray-700">{t('api.job.created')}</span> {formatDate(job.created_at)}
                </div>
                {job.completed_at && (
                  <div>
                    <span className="font-medium text-gray-700">{t('api.job.completed')}</span> {formatDate(job.completed_at)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default APIv1Jobs

